/*
 * ttg-hint-pulse.js — Hinweis-Animation für den Link „‹ Mehr Motive entdecken".
 * ---------------------------------------------------------------------------
 * Zweck (Jens 08.08.2026): Der Rücksprung ins Kategorie-Menü wird übersehen.
 * Alle 15 s ein kurzer Blink + eine horizontale Drehung (rotateY 360°), damit
 * er auffällt — aber nur solange das sinnvoll ist (siehe Abbruchbedingungen).
 *
 * WARUM EIN EIGENES ASSET und kein Engine-Code: CLAUDE.md §3 — Feature-Code
 * gehört in eigene Assets, nicht in ttg-engine.js. Dieses Skript fasst die
 * Engine nicht an; es liest nur `host.engine.state` (gleiches Muster wie
 * ttg-crosssell.js) und arbeitet ansonsten rein auf dem DOM.
 *
 * DIE ZENTRALE FALLE — der Button ist NICHT stabil:
 * Die Engine rendert den Motiv-Schritt bei jeder Zustandsänderung komplett neu
 * (`renderSteps` → innerHTML). Der Button wird dabei durch einen NEUEN Knoten
 * ersetzt. Alles Naive scheitert daran:
 *   • eine einmal gesetzte Klasse ist beim nächsten Render weg,
 *   • eine einmal gemerkte Referenz zeigt auf einen toten Knoten,
 *   • ein einmal `observe()`-ter IntersectionObserver beobachtet ins Leere und
 *     meldet für immer „nicht sichtbar" → die Animation liefe nie.
 * Deshalb hält ein MutationObserver das Ziel dauerhaft frisch (_syncTarget) und
 * hängt den IntersectionObserver bei jedem Knotentausch neu ein. Der Takt-Timer
 * liest danach nur noch Flags und ist entsprechend billig (INP).
 *
 * ABBRUCHBEDINGUNGEN (alle Pflicht, alle unten einzeln kommentiert):
 *   1. prefers-reduced-motion: reduce → gar keine Animation.
 *   2. nicht im Viewport → pausiert, OHNE einen Durchlauf zu verbrauchen.
 *   3. Klick auf den Link → dauerhaft aus (für diese Seitensitzung).
 *   4. nach 6 Durchläufen → dauerhaft aus.
 *   5. Motivraster aufgeklappt → pausiert, OHNE Durchlauf zu verbrauchen.
 */
(function () {
  'use strict';

  var HOST_SEL = 'ttg-customizer';
  var LINK_SEL = '[data-action="bucket-back"]';
  var EXPAND_SEL = '[data-action="motif-expand"]';
  var CLASS = 'ttg-hint-pulse';

  var FIRST_DELAY_MS = 8000;   // erster Lauf NICHT sofort beim Laden
  var INTERVAL_MS = 15000;     // danach alle 15 s
  var MAX_RUNS = 6;            // Abbruchbedingung 4
  var ANIM_MS = 900;           // muss zur CSS-Dauer passen (Notbremse unten)

  var host = document.querySelector(HOST_SEL);
  if (!host) return;

  /* Abbruchbedingung 1 — „Reduzierte Bewegung": hier hart aussteigen, nicht nur
     im CSS. Würde die Klasse trotzdem gesetzt, käme wegen `animation: none` NIE
     ein `animationend` — die Klasse bliebe für immer am Knoten hängen. */
  var mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  if (mq && mq.matches) return;

  var runs = 0;
  var stopped = false;
  var animating = false;
  var observed = null;         // aktuell beobachteter Button-Knoten
  var inView = false;          // vom IntersectionObserver gepflegt
  var expandedByClick = false; // Fallback, falls die Engine-State-Lesung nicht greift
  var timer = null;
  var mo = null;
  var rafPending = false;

  /* threshold 0.6: „im Viewport" heißt hier mehrheitlich sichtbar. Ein Button,
     der zu 5 % am Rand hereinlugt, ist kein Blickfang — dafür zu animieren wäre
     verschenkt und verbrauchte einen der sechs Durchläufe. */
  var io = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].target === observed) inView = entries[i].isIntersecting;
        }
      }, { threshold: 0.6 })
    : null;

  /* Ohne IntersectionObserver (sehr alte Browser) bleibt die Sichtbarkeitsprüfung
     die geforderte Abbruchbedingung — dann lieber gar nicht animieren als
     ungeprüft. Kein Fallback auf getBoundingClientRect: das wäre eine andere,
     teurere Semantik (Layout-Lesung im Timer). */
  if (!io) return;

  function syncTarget() {
    var el = host.querySelector(LINK_SEL);
    if (el === observed) return;
    if (observed) io.unobserve(observed);
    observed = el;
    /* Bis der Observer das erste Mal meldet, gilt „nicht sichtbar". Das ist der
       Grund, warum _syncTarget am MutationObserver hängt und nicht erst im Tick
       läuft: so hat der IO zwischen Re-Render und nächstem Tick reichlich Zeit,
       den echten Zustand zu liefern. */
    inView = false;
    if (el) io.observe(el);
  }

  /* Abbruchbedingung 5 — Motivraster aufgeklappt („Alle N Motive anzeigen"
     wurde geklickt). Primärquelle ist der Engine-Zustand: `motifExpandedFor`
     hält den Bucket, für den aufgeklappt wurde; ist das der aktuelle Bucket,
     stöbert der Kunde gerade im vollen Raster und will nicht angeblinkt werden.
     Der Zustand setzt sich beim Bucket-Wechsel von selbst zurück — ein reines
     Klick-Flag könnte das nicht und bliebe fälschlich „aufgeklappt". Das
     Klick-Flag dient nur als Fallback, falls die Engine (noch) nicht hängt. */
  function gridExpanded() {
    var eng = host.engine;
    var st = eng && eng.state;
    if (st && typeof st.motifExpandedFor !== 'undefined') {
      return !!st.motifExpandedFor && st.motifExpandedFor === st.bucket;
    }
    return expandedByClick;
  }

  function stop() {
    if (stopped) return;
    stopped = true;
    if (timer) { clearInterval(timer); timer = null; }
    if (mo) { mo.disconnect(); mo = null; }
    if (observed) io.unobserve(observed);
    io.disconnect();
    observed = null;
  }

  function pulse(el) {
    animating = true;
    /* Gemeinsame Bremse aller TTG-Hinweise (seit 08.08.2026): Solange hier etwas
       blinkt, hält sich jeder andere Hinweis zurück — und umgekehrt (Abfrage im
       tick()). Zwei gleichzeitig zappelnde Elemente heben sich gegenseitig auf.
       Zweiter Nutzer des Flags: der Raster-Hinweis im Motivbuch
       (assets/ttg-motivbuch.js). Beide leben auf verschiedenen Seitentypen, das
       Flag ist die Absicherung für den Fall, dass sich das einmal ändert. */
    window.__ttgHintActive = true;
    var done = function () {
      el.removeEventListener('animationend', done);
      el.classList.remove(CLASS);
      animating = false;
      window.__ttgHintActive = false;
    };
    el.addEventListener('animationend', done);
    el.classList.add(CLASS);
    /* Notbremse: Wird der Knoten mitten in der Animation durch ein Re-Render
       ersetzt, feuert `animationend` nie — ohne diesen Timer bliebe `animating`
       für immer true und der Hinweis käme nie wieder. Der weggeworfene Knoten
       selbst ist egal, er ist samt Klasse aus dem DOM. */
    setTimeout(function () {
      if (!animating) return;
      animating = false;
      try { done(); } catch (e) { /* Knoten weg — nichts zu tun */ }
    }, ANIM_MS + 400);
  }

  function tick() {
    if (stopped) return;
    syncTarget();
    if (animating) return;
    var el = observed;
    if (!el || !el.isConnected) return;   // Schritt gar nicht sichtbar (z. B. andere Sektion offen)
    if (!inView) return;                  // Abbruchbedingung 2 — pausiert, kein Zählerverbrauch
    if (gridExpanded()) return;           // Abbruchbedingung 5 — pausiert, kein Zählerverbrauch
    if (window.__ttgHintActive) return;   // anderer TTG-Hinweis läuft — pausiert, kein Zählerverbrauch
    runs += 1;
    pulse(el);
    if (runs >= MAX_RUNS) stop();         // Abbruchbedingung 4 — danach nie wieder
  }

  /* Abbruchbedingung 3 — Klick auf den Link schaltet dauerhaft ab. Der Listener
     hängt am Dokument (capture), NICHT am Button: der Button überlebt kein
     Re-Render, das Dokument schon. Gleiches gilt für den Aufklapp-Fallback. */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest(LINK_SEL)) { stop(); return; }
    if (t.closest(EXPAND_SEL)) expandedByClick = true;
  }, true);

  /* Ziel bei jedem Re-Render nachziehen. Der Callback selbst bleibt trivial und
     per rAF entprellt — `renderSteps` löst viele Mutationen auf einmal aus, und
     dieses Skript darf die INP-Messung des Konfigurators nicht belasten. */
  mo = new MutationObserver(function () {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(function () {
      rafPending = false;
      if (!stopped) syncTarget();
    });
  });
  mo.observe(host, { childList: true, subtree: true });

  syncTarget();
  /* Erster Lauf nach 8 s, nicht sofort beim Laden: direkt beim Öffnen hat der
     Kunde den Schritt noch gar nicht gelesen. */
  setTimeout(function () {
    if (stopped) return;
    tick();
    if (!stopped) timer = setInterval(tick, INTERVAL_MS);
  }, FIRST_DELAY_MS);
})();
